import { useMsal } from '@azure/msal-react';
import { helyxLogoWhite } from '@celito.clients/assets';
import { ROUTES } from '@celito.clients/enums';
import { useEntraRoles } from '@celito.clients/hooks';
import { Footer, UserAvatar } from '@celito.clients/shared';
import { useTheme } from '@celito.clients/theme';
import { UserTypes } from '@celito.clients/types';
import {
  Button,
  DrawerBody,
  InlineDrawer,
  mergeClasses,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  PanelLeftContractRegular,
  PanelLeftExpandRegular,
} from '@fluentui/react-icons';
import { Suspense, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { FooterData } from './mock';
import SessionTimeout from './session-time-out';
import { useTenantAdministrationLayoutStyles } from './tenant-administration-layout.styles';

const TenantAdministrationLayout = () => {
  const styles = useTenantAdministrationLayoutStyles();
  const { cssVariables } = useTheme();
  const { instance } = useMsal();
  const { account } = useEntraRoles();
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavigationOpen, setIsNavigationOpen] = useState(true);
  const tenantManagementPath = `/${ROUTES.TENANT_MANAGEMENT}`;
  const tenantsSelected =
    location.pathname === tenantManagementPath ||
    location.pathname.startsWith(`${tenantManagementPath}/`);

  // The platform has no row in a customer User table. Build the display-only avatar identity from
  // the already verified Entra account instead of mounting UserProvider and calling `/user/me`.
  const user = useMemo<UserTypes | null>(() => {
    if (!account) return null;
    const label = account.name || account.username || 'Super Admin';
    const name = account.localAccountId || account.homeAccountId;
    const identity = { name, label, email: account.username };
    return {
      id: -1,
      name,
      label,
      email: account.username,
      createdBy: identity,
      modifiedBy: identity,
    };
  }, [account]);

  const signOut = () => {
    if (account) void instance.logoutRedirect({ account });
  };

  return (
    <div className={styles.shell} style={{ ...cssVariables }}>
      <SessionTimeout />
      <header className={styles.header}>
        <Link
          to={`/${ROUTES.TENANT_MANAGEMENT}`}
          className={styles.logoLink}
          aria-label="Helyx tenant administration home"
        >
          <img className={styles.logo} src={helyxLogoWhite} alt="Helyx" />
        </Link>
        <div className={styles.profile}>
          <UserAvatar
            dataTestId="tenant-administration-user"
            onSignOutClick={signOut}
            user={user}
          />
        </div>
      </header>

      <div className={styles.contentArea}>
        {isNavigationOpen ? (
          <InlineDrawer
            className={styles.navigationDrawer}
            open
            position="start"
            aria-label="Platform navigation"
          >
            <DrawerBody className={styles.navigationBody}>
              <div className={styles.navigationHeader}>
                <Text className={styles.navigationLabel} weight="semibold">
                  Super Admin
                </Text>
                <Button
                  appearance="subtle"
                  aria-label="Collapse navigation"
                  icon={<PanelLeftContractRegular />}
                  onClick={() => setIsNavigationOpen(false)}
                  title="Collapse navigation"
                />
              </div>
              <Button
                appearance="subtle"
                aria-current={tenantsSelected ? 'page' : undefined}
                className={mergeClasses(
                  styles.navigationItem,
                  tenantsSelected && styles.navigationItemSelected
                )}
                icon={<BuildingRegular />}
                onClick={() => navigate(tenantManagementPath)}
              >
                Tenants
              </Button>
            </DrawerBody>
          </InlineDrawer>
        ) : (
          <aside className={styles.collapsedNavigation}>
            <Button
              appearance="subtle"
              aria-label="Expand navigation"
              icon={<PanelLeftExpandRegular />}
              onClick={() => setIsNavigationOpen(true)}
              title="Expand navigation"
            />
            <Button
              appearance="subtle"
              aria-current={tenantsSelected ? 'page' : undefined}
              aria-label="Tenants"
              className={mergeClasses(
                styles.collapsedNavigationItem,
                tenantsSelected && styles.navigationItemSelected
              )}
              icon={<BuildingRegular />}
              onClick={() => navigate(tenantManagementPath)}
              title="Tenants"
            />
          </aside>
        )}

        <main className={styles.main} id="applicationMainContainer">
          <div className={styles.mainBody}>
            <Suspense
              fallback={<Spinner label="Loading tenant administration" />}
            >
              <Outlet />
            </Suspense>
          </div>
          <footer className={styles.footer}>
            <Footer {...FooterData} />
          </footer>
        </main>
      </div>
    </div>
  );
};

export default TenantAdministrationLayout;
